<script setup>
import { useServerStatus } from '@/composables/serverStatus';
import { computed } from 'vue';
import { useTheme } from '@/layout/composables/layout';

const offlineHtml = String.raw`<!DOCTYPE html>
<html lang="en" class="app">
<head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>CORECRY - Server Offline</title>
    <link rel="icon" href="/favicon.ico" />
    <style>
        html,
        body {
            margin: 0;
            padding: 0;
            font-family: 'Lato', sans-serif;
            background-color: var(--theme-page, #212121) !important;
            color: var(--theme-text, #ffffff) !important;
            min-height: 100vh;
            min-width: 100vw;
            overflow: hidden;
        }

        .page-bg {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            min-width: 100vw;
            overflow: hidden;
            background-color: var(--theme-page, #212121) !important;
        }

        @media (max-width: 768px) {
            .page-bg { min-height: 100dvh; }
            .content-container > .mb-4 { margin-bottom: 0; }
        }

        .page-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        }

        .content-container {
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .form-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        @media (max-width: 768px) {
            .form-placeholder { display: none; }
        }

        .mb-8 {
            margin-bottom: 2rem;
        }

        .mb-4 {
            margin-bottom: 1rem;
        }

        .w-32 {
            width: 7rem; /* 112px to match login page */
        }

        .mx-auto {
            display: block;
            margin-left: auto;
            margin-right: auto;
        }

        .shrink-0 {
            flex-shrink: 0;
        }

        .logo-themed {
            /* No filter needed - using white SVG directly */
        }

        .login-logo-img {
            animation: glitch 4s infinite steps(1, end);
            position: relative;
            filter: var(--theme-offline-logo-filter, brightness(1)) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3)) !important;
        }

        @keyframes glitch {
            0%, 70% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }

            70.5% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(0.3) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 0.3;
            }
            71% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }

            71.5%, 83% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }

            83.5% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(0.1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 0.1;
            }
            84% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(0.8) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 0.8;
            }
            84.5% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(0.2) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 0.2;
            }
            85% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }

            85.5%, 92% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }

            92.5% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(0.4) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 0.4;
            }
            93% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }
            93.5% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(0.6) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 0.6;
            }
            94% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }

            94.5%, 100% {
                transform: translateX(0) translateY(0) scale(1);
                filter: var(--theme-offline-logo-filter, brightness(1)) hue-rotate(0deg) brightness(1) contrast(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
                opacity: 1;
            }
        }

        .login-logo-img::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDk1IiBoZWlnaHQ9IjQ5NSIgdmlld0JveD0iMCAwIDQ5NSA0OTUiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTI0Ny43MjMgMzY0LjkyOEwzOTYuMTMzIDEwNy44NzRIOTkuMzEyNUwyNDcuNzIzIDM2NC45MjhaIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjcwIi8+PC9zdmc+') no-repeat center center;
            background-size: contain;
            opacity: 0;
            animation: glitch-overlay 4s infinite steps(1, end);
            mix-blend-mode: difference;
            /* No filter needed - using white SVG directly */
        }

        @keyframes glitch-overlay {
            0%, 70% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }

            70.5% {
                opacity: 0.2;
                transform: translateX(0) translateY(0);
            }
            71% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }

            71.5%, 83% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }

            83.5% {
                opacity: 0.4;
                transform: translateX(0) translateY(0);
            }
            84% {
                opacity: 0.1;
                transform: translateX(0) translateY(0);
            }
            84.5% {
                opacity: 0.3;
                transform: translateX(0) translateY(0);
            }
            85% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }

            85.5%, 92% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }

            92.5% {
                opacity: 0.3;
                transform: translateX(0) translateY(0);
            }
            93% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }
            93.5% {
                opacity: 0.2;
                transform: translateX(0) translateY(0);
            }
            94% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }

            94.5%, 100% {
                opacity: 0;
                transform: translateX(0) translateY(0);
            }
        }

        .offline-text {
            font-size: 1.5rem;
            font-weight: 600;
            text-align: center;
            color: var(--theme-text, #ffffff) !important;
            letter-spacing: 0.02em;
            user-select: none;
        }

        .offline-text .char {
            display: inline-block;
            transition: color 0.2s ease, text-shadow 0.2s ease, opacity 0.2s ease;
        }

        /* Blink animation without blur/glow for a single character */
        @keyframes blink-letter {
            0%   { opacity: 1; }
            10%  { opacity: 0; }
            25%  { opacity: 1; }
            40%  { opacity: 0; }
            55%  { opacity: 1; }
            70%  { opacity: 0; }
            85%  { opacity: 1; }
            100% { opacity: 1; }
        }

        .offline-text .char.flicker {
            animation: blink-letter 0.6s linear both;
        }

    </style>

    <script>
        // Setup flickering animation for random letters in the Offline text
        window.addEventListener('DOMContentLoaded', () => {
            const el = document.querySelector('.offline-text');
            if (!el) return;

            const text = el.textContent || '';
            el.textContent = '';

            // Wrap each character into a span for independent animation
            for (const ch of text) {
                const span = document.createElement('span');
                span.className = 'char';
                span.textContent = ch === ' ' ? '\u00A0' : ch;
                el.appendChild(span);
            }

            const chars = Array.from(el.querySelectorAll('.char'))
                .filter(s => (s.textContent || '').trim().length > 0);

            if (chars.length === 0) return;

            const flickerOnce = () => {
                const i = Math.floor(Math.random() * chars.length);
                const target = chars[i];
                if (!target) return;
                // If already flickering, skip to avoid stacking
                if (target.classList.contains('flicker')) return;

                target.classList.add('flicker');
                const remove = () => {
                    target.classList.remove('flicker');
                    target.removeEventListener('animationend', remove);
                };
                target.addEventListener('animationend', remove);
            };

            const loop = () => {
                flickerOnce();
                // Randomize delay to simulate power instability
                const delay = 450 + Math.random() * 1250; // 450ms..1700ms
                setTimeout(loop, delay);
            };
            loop();

        });
    </scr` + 'ipt>' + `
</head>
<body>
    <div class="page-bg">
        <div class="page-content">
            <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDk1IiBoZWlnaHQ9IjQ5NSIgdmlld0JveD0iMCAwIDQ5NSA0OTUiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTI0Ny43MjMgMzY0LjkyOEwzOTYuMTMzIDEwNy44NzRIOTkuMzEyNUwyNDcuNzIzIDM2NC45MjhaIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjcwIi8+PC9zdmc+" alt="logo" class="mb-8 w-32 shrink-0 mx-auto logo-themed login-logo-img" />
            
            <div class="content-container">
                <!-- QR код / Telegram кнопка - здесь должен быть текст Offline -->
                <div class="mb-4">
                    <div class="offline-text">Offline</div>
                </div>
                
                <!-- Зарезервированное место для формы OTP (фиксированная высота) -->
                <div class="form-placeholder" style="min-height: 140px;">
                    <!-- Пустое место для соответствия структуре -->
                </div>
            </div>
        </div>
    </div>
</body>
</html>`;

const { online } = useServerStatus();
const { effectiveTheme } = useTheme();
const themedOfflineHtml = computed(() => {
    const dark = effectiveTheme.value === 'dark';
    const colors = getComputedStyle(document.documentElement);
    const names = ['page', 'text', 'offline-logo-filter'];
    const palette = dark ? '' : names.map(name => `--theme-${name}:${colors.getPropertyValue(`--theme-${name}`)};`).join('');
    return offlineHtml.replace('<html lang="en" class="app">', `<html lang="en" class="app${dark ? ' app-dark' : ''}">`)
        .replace('<style>', `<style>:root{color-scheme:${effectiveTheme.value};${palette}}`);
});
</script>

<template>
    <teleport to="body">
        <div v-if="!online" data-corecry-offline class="fixed inset-0 z-[1000000]">
            <iframe :srcdoc="themedOfflineHtml" class="w-full h-full border-0" src="about:blank"></iframe>
        </div>
    </teleport>
</template>
